import { IconNames } from '@dixa/lib-shared-habla';
import { SearchUserEnriched } from 'lib-search-types';

import { getStatus } from './getStatus';
import { getWorkingChannels } from './getWorkingChannels';

export type PresenceIndicator = {
  icon: IconNames;
  tooltip: string;
};

type PresenceFlags = {
  hasActivePhoneConversation: boolean;
  isInACW: boolean;
  simultaneousRingEnabled: boolean;
};

export type PresenceIconFlags = PresenceFlags & {
  isWorkingWithoutActiveChannel: boolean;
};

export const getPresenceFlags = (
  member: SearchUserEnriched,
): PresenceFlags => ({
  hasActivePhoneConversation:
    (member.workloads_by_channel?.pstn_phone || 0) > 0,
  isInACW: member.has_acw ?? false,
  simultaneousRingEnabled: member.simultaneous_ring ?? false,
});

/**
 * Single source of truth for the presence-state icon priority across the team
 * overview, the new sidebar, and the legacy sidebar.
 */
export const getPresenceIcon = (flags: PresenceIconFlags): IconNames => {
  if (flags.hasActivePhoneConversation) return 'phone';
  if (flags.isInACW) return 'circle-z';
  if (flags.simultaneousRingEnabled) return 'simultaneous';
  if (flags.isWorkingWithoutActiveChannel) return 'circle';
  return 'circle-filled';
};

/**
 * Companion to `getPresenceIcon` — composes the tooltip string for a presence
 * indicator.
 */
export const getPresenceTooltip = (
  flags: PresenceIconFlags,
  statusBase: string,
): string => {
  let tooltip = flags.isWorkingWithoutActiveChannel
    ? 'Online without active channel'
    : statusBase;

  if (flags.hasActivePhoneConversation) {
    tooltip += ' - On a call';
  } else if (flags.isInACW) {
    tooltip += ' - Wrap-up time';
  } else if (flags.simultaneousRingEnabled) {
    tooltip += ' - Simultaneous ring enabled';
  }

  return tooltip;
};

export const getPresenceIndicator = (
  member: SearchUserEnriched,
): PresenceIndicator => {
  const status = getStatus(member);
  const workingChannels = getWorkingChannels(member);
  const { hasActivePhoneConversation, isInACW, simultaneousRingEnabled } =
    getPresenceFlags(member);

  const isWorking = status === 'working';
  const isWorkingWithoutActiveChannel =
    isWorking && workingChannels.every(({ working }) => !working);

  const iconFlags: PresenceIconFlags = {
    hasActivePhoneConversation,
    isInACW,
    simultaneousRingEnabled,
    isWorkingWithoutActiveChannel,
  };

  return {
    icon: getPresenceIcon(iconFlags),
    tooltip: getPresenceTooltip(iconFlags, status),
  };
};
